import type { SupportedCountry } from "@/constants/country";
import { defaultNS, useTranslation } from "@/lib/i18n";
import { useState } from "react";
import Combobox from "../Combobox";
import Icon from "../Icon/Icon";
import { Button } from "../ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover";
import { useStateProvinces } from "./useStateProvinces";

export interface SelectStateProvinceProps
  extends React.SelectHTMLAttributes<HTMLSelectElement> {
  country?: SupportedCountry;
  value?: string;
  onValueChange: (value: string) => void;
}

interface StateProvince {
  key?: string;
  label: string;
  value: string;
  disabled?: boolean;
}

const SelectStateProvince = ({
  country,
  value,
  onValueChange,
}: SelectStateProvinceProps) => {
  const { t } = useTranslation(defaultNS, {
    keyPrefix: "component.selectStateProvince",
  });
  const [open, setOpen] = useState(false);
  const [searchText, setSearchText] = useState("");
  const { stateProvinces = [], isLoading } = useStateProvinces(country);

  const selectedStateProvince = stateProvinces.find((c) => c.value === value);

  const handleStateProvinceChange = (value: string | null) => {
    onValueChange(value ?? "");
    if (value) {
      setOpen(false);
    }
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className="w-44 justify-between"
          disabled={isLoading}
        >
          {selectedStateProvince?.label || t("placeholder")}
          <Icon
            iconName="ChevronDownIcon"
            className="ml-2 shrink-0 opacity-50"
          />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-60 p-0">
        <Combobox<StateProvince>
          type="single"
          options={stateProvinces}
          value={selectedStateProvince?.value || ""}
          onValueChange={handleStateProvinceChange}
          search={searchText}
          onSearchChange={setSearchText}
          searchPlaceholder={t("searchPlaceholder")}
          noResultsMessage={t("noResultsMessage")}
          showCheckbox={false}
        />
      </PopoverContent>
    </Popover>
  );
};

export default SelectStateProvince;
